/**
 * Edit Version Tags Dialog
 *
 * Dialog for managing custom tags on a version.
 * Uses the shared TagSelectorDialog pattern with color picker support.
 *
 * Features:
 * - View currently assigned tags (filtered to versions)
 * - Add/remove tags from version
 * - Create new tags inline with color picker
 *
 * Note: System tags (current/previous) are managed via VersionActionMenu,
 * not through this dialog. This dialog only handles custom user tags.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { TagSelectorDialog, type TagSelectorItem, type CreateTagOptions } from '@loader/ui';

import {
  useTagsQuery,
  useSetVersionTagsMutation,
  useCreateTagMutation,
} from '@/features/organization/hooks';
import { useToastStore } from '@/store/useToasts';

// ============================================================================
// TYPES
// ============================================================================

interface EditVersionTagsDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog should close */
  onOpenChange: (open: boolean) => void;
  /** Version to edit tags for */
  version: {
    id: string;
    file_name: string;
    tags: Array<{
      id: string;
      name: string;
      color: string | null;
    }>;
  } | null;
  /** Optional: Table ID for optimistic updates */
  tableId?: string;
  /** Callback when tags are updated */
  onSaved?: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Dialog for managing custom tags on a version
 */
export function EditVersionTagsDialog({
  open,
  onOpenChange,
  version,
  tableId,
  onSaved,
}: EditVersionTagsDialogProps) {
  const { error: showError, success: showSuccess } = useToastStore();

  // Queries - fetch all tags (all tags are for versions now)
  const { data: allTags, isLoading: isLoadingTags } = useTagsQuery();

  // Mutations
  const setVersionTagsMutation = useSetVersionTagsMutation();
  const createTagMutation = useCreateTagMutation();

  // Local state
  const currentTagIds = useMemo(() => {
    return version?.tags?.map((tag) => tag.id) || [];
  }, [version]);

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(currentTagIds);
  const [isSaving, setIsSaving] = useState(false);

  // Convert tags to TagSelectorItem format
  const items: TagSelectorItem[] = useMemo(() => {
    return (allTags ?? []).map((tag) => ({
      id: tag.id,
      name: tag.name,
      color: tag.color,
      count: tag.version_count,
    }));
  }, [allTags]);

  // Handle tag creation
  const handleCreate = useCallback(
    async (options: CreateTagOptions): Promise<string | undefined> => {
      try {
        const result = await createTagMutation.mutateAsync({
          name: options.name,
          color: options.color,
        });
        showSuccess('Tag created');
        return result?.tag?.id;
      } catch (err) {
        showError('Failed to create tag', err instanceof Error ? err.message : 'Unknown error');
        return undefined;
      }
    },
    [createTagMutation, showSuccess, showError]
  );

  // Handle save
  const handleSave = useCallback(async () => {
    if (!version) return;

    setIsSaving(true);
    try {
      // Build newTags for optimistic updates
      const newTags = selectedTagIds
        .map((id) => allTags?.find((t) => t.id === id))
        .filter((t): t is NonNullable<typeof t> => t != null)
        .map((t) => ({ id: t.id, name: t.name, color: t.color }));

      await setVersionTagsMutation.mutateAsync({
        versionId: version.id,
        tagIds: selectedTagIds,
        tableId,
        newTags,
      });
      showSuccess('Tags updated');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      showError('Failed to update tags', err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsSaving(false);
    }
  }, [
    version,
    selectedTagIds,
    allTags,
    tableId,
    setVersionTagsMutation,
    showSuccess,
    showError,
    onSaved,
    onOpenChange,
  ]);

  // Handle close - reset state
  const handleOpenChange = useCallback(
    (openState: boolean) => {
      if (!openState) {
        setSelectedTagIds(currentTagIds);
        onOpenChange(false);
      }
    },
    [currentTagIds, onOpenChange]
  );

  // Update selected tags when version changes or dialog opens
  useEffect(() => {
    if (open) {
      setSelectedTagIds(currentTagIds);
    }
  }, [open, currentTagIds]);

  return (
    <TagSelectorDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Manage Tags"
      description={`Add or remove tags for ${version?.file_name || 'this version'}.`}
      items={items}
      selectedIds={selectedTagIds}
      onSelectionChange={setSelectedTagIds}
      showColors={true}
      allowCreate={true}
      onCreate={handleCreate}
      createPlaceholder="New tag..."
      isSaving={isSaving}
      onSave={handleSave}
      isLoading={isLoadingTags}
    />
  );
}

export default EditVersionTagsDialog;
